import { QtmButton, QtmIcon } from '@qtm/react';
import { useUpdateUserMutation } from 'api/UsersEndPoints';
import dayjs from 'dayjs';
import { ReactNode } from 'react';
import DatePicker from 'react-datepicker';
import { Link } from 'react-router-dom';
import { User } from 'shared/model/user.model';

const frontUrl = import.meta.env.VITE_FRONT_URL;

export const availablePageSizes = [10, 20, 50, 100];

export interface TableColumn<T> {
  name: keyof T;
  header: string;
  type: 'string' | 'number' | 'date';
  render?: (value: any, row?: any) => ReactNode | string;
  style?: React.CSSProperties;
}

export interface UserTableModel extends User {
  connections: number;
  timespent: number;
}

export const userTableColumns: TableColumn<UserTableModel>[] = [
  {
    name: 'firstName',
    header: 'First Name',
    type: 'string',
  },
  {
    name: 'lastName',
    header: 'Last Name',
    type: 'string',
  },
  {
    name: 'company',
    header: 'Company',
    type: 'string',
    style: {
      width: '120px',
      minWidth: '120px',
    },
  },
  {
    name: 'email',
    header: 'Email',
    type: 'string',
    style: { width: '320px' },
    render: (value: string, user: User) => {
      return (
        <Link className="font-bold" to={`/users/${user.id}/stats`} title={value}>
          {value}
        </Link>
      );
    },
  },
  {
    name: 'connections',
    header: 'Connections',
    type: 'number',
    style: {
      width: '120px',
      minWidth: '120px',
    },
  },
  {
    name: 'timespent',
    header: 'Time spent',
    type: 'number',
    render: (value: number) => {
      if (value === 0) {
        return 0;
      }
      return dayjs.duration(value, 'seconds').humanize();
    },
  },
  {
    name: 'token',
    header: 'Link',
    type: 'string',
    style: {
      width: '80px',
      minWidth: '80px',
    },
    render: (value: string | undefined) => {
      if (!value) {
        return '';
      }

      const url = frontUrl + '?token=' + value;

      const onCopy = () => {
        navigator.clipboard.writeText(url);
      };

      const onOpen = () => {
        window.open(url);
      };

      return (
        <div className="flex">
          <QtmButton
            variant="ghost"
            color="neutral"
            size="small"
            onClick={onCopy}
            title="Copy link"
          >
            <QtmIcon icon="link" />
          </QtmButton>
          <QtmButton
            variant="ghost"
            color="neutral"
            size="small"
            onClick={onOpen}
            title="open link"
          >
            <QtmIcon icon="open_in_new" />
          </QtmButton>
        </div>
      );
    },
  },
  {
    name: 'expirationDate',
    header: 'Expiration',
    type: 'date',
    style: {
      width: '120px',
      minWidth: '120px',
    },
    render: (value: string | undefined, user: User) => {
      const [updateUser, { isLoading }] = useUpdateUserMutation();

      if (!value) return '';

      const date = dayjs(value);
      const now = dayjs();

      const onChange = (newDate: Date | null) => {
        if (!newDate) return;
        const expirationDate = dayjs(newDate).endOf('day');

        updateUser({
          id: user.id,
          token: user.token,
          expirationDate: expirationDate.toISOString(),
        });
      };

      return (
        <DatePicker
          minDate={now.toDate()}
          selected={date.toDate()}
          onChange={onChange}
          customInput={<div>{date.format('DD/MM/YYYY')}</div>}
        />
      );
    },
  },
];
